'use client'

import { queryOptions, useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { responseErrorMessage } from '@/lib/api/error-message'
import type { BillingGateResponse } from '@/lib/billing/gate'
import { canViewAiReasoning } from '@/lib/permissions/ai-reasoning'
import {
  canUseFinancialCapability,
  type FinancialCapability,
} from '@/lib/permissions/financial-capabilities'
import { canViewInsightsResults } from '@/lib/permissions/insights-results'
import { applyInboxDepartmentRestriction } from '@/lib/permissions/inbox-departments'
import type { PermissionAction, PermissionResource, WorkspaceRole } from '@/lib/permissions/matrix'
import { matrixHasPermission, ROLE_PERMISSIONS } from '@/lib/permissions/matrix'
import {
  canViewOperationsModule,
  type OperationsModule,
} from '@/lib/permissions/operations-modules'
import {
  type PropertySensitiveFieldGroupAccess,
  resolvePropertySensitiveFieldGroups,
} from '@/lib/permissions/property-field-visibility'
import { canViewReservationSensitiveData } from '@/lib/permissions/reservation-sensitive-data'
import type { TaskPermissionConfig } from '@/lib/tasks/task-permissions'
import type { WorkforceShiftMode } from '@/lib/workforce/member'
import { canSelfClaimShifts, isFieldOpsWorker } from '@/lib/workforce/member'
import { useUserId } from './use-user'
import {
  shouldRetryWorkspaceBootstrapQuery,
  useWorkspaceId,
  useWorkspaceSettings,
  workspaceBootstrapFetchError,
} from './use-workspace'

// =============================================================================
// TYPES
// =============================================================================

export type { PermissionAction, PermissionResource, WorkspaceRole }
// Re-export the matrix types so existing importers of `@/hooks/use-permissions`
// keep working. The canonical definitions live in `@/lib/permissions/matrix`.
export { ROLE_PERMISSIONS }

// Permission check result
interface PermissionCheckResult {
  allowed: boolean
  reason?: string
}

interface PermissionHookOptions {
  enabled?: boolean
}

type WorkspaceMemberHookOptions = PermissionHookOptions & {
  workspaceId?: string | null
}

type WorkspaceMemberHookArg = WorkspaceMemberHookOptions | string | null | undefined

function workspaceMemberHookOptions(arg: WorkspaceMemberHookArg): WorkspaceMemberHookOptions {
  if (typeof arg === 'string' || arg === null) {
    return { workspaceId: arg }
  }
  return arg ?? {}
}

// Custom role data from the join
export interface CustomRoleMemberData {
  color: string
  icon: string
  id: string
  name: string
  permissions: TaskPermissionConfig
  role_type: string
  slug: string
}

// Workspace member data
export interface WorkspaceMemberData {
  billing_gate?: BillingGateResponse | null
  created_at: string
  custom_role_id: string | null
  custom_roles: CustomRoleMemberData | null
  department_members: Array<{
    department_id: string
    /** Whether this member leads the department (PROD-3935, Option B). */
    is_lead?: boolean
    created_at: string
    department: {
      id: string
      name: string
      slug: string
      color: string | null
      icon: string | null
      is_field_ops: boolean
      is_default: boolean
    } | null
  }>
  id: string
  is_active: boolean
  permissions: Record<string, unknown>
  role: WorkspaceRole
  shift_mode: WorkforceShiftMode
  user_id: string
  workspace_id: string
}

// Workspace settings shape for role permissions
interface WorkspaceSettingsWithRolePerms {
  inboxAccess?: Record<string, string[]>
  rolePermissions?: Record<string, TaskPermissionConfig>
  [key: string]: unknown
}

// =============================================================================
// PERMISSION RESOLUTION
// =============================================================================

/**
 * Resolve permissions for a member using the shared three-tier chain in
 * `@/lib/permissions/matrix`:
 * 1. Custom role? Use custom_roles.permissions directly
 * 2. Built-in role? Check workspace.settings.rolePermissions override
 * 3. Fall back to hardcoded ROLE_PERMISSIONS defaults
 *
 * ROLE_PERMISSIONS defaults are shared from `@/lib/permissions/matrix`; the
 * server-side counterpart is `@/lib/auth/server-permissions`.
 */
const resolvePermissions = (
  member: WorkspaceMemberData,
  workspaceSettings?: WorkspaceSettingsWithRolePerms | null
): TaskPermissionConfig => {
  // 1. Custom role permissions take priority
  if (member.custom_role_id && member.custom_roles) {
    return member.custom_roles.permissions ?? {}
  }

  // 2. Check workspace-level overrides for built-in roles
  const role = member.role
  const overrides = workspaceSettings?.rolePermissions?.[role]
  if (overrides) {
    return {
      ...(ROLE_PERMISSIONS[role] ?? {}),
      ...overrides,
    }
  }

  // 3. Fall back to hardcoded defaults
  return ROLE_PERMISSIONS[role] ?? {}
}

// =============================================================================
// QUERY OPTIONS
// =============================================================================

export function workspaceMemberQueryOptions(workspaceId: string, userId: string) {
  return queryOptions({
    queryKey: ['workspaceMember', workspaceId, userId],
    queryFn: async () => {
      const response = await fetch(`/api/workspaces/${workspaceId}/member`, {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      })

      if (!response.ok) {
        throw await workspaceBootstrapFetchError(response, 'Failed to load workspace member')
      }
      return response.json() as Promise<WorkspaceMemberData | null>
    },
    retry: shouldRetryWorkspaceBootstrapQuery,
    staleTime: 1000 * 60 * 10, // 10 minutes
  })
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Get the current user's workspace membership data.
 */
export function useWorkspaceMember(options?: WorkspaceMemberHookArg) {
  const routeWorkspaceId = useWorkspaceId()
  const { workspaceId: workspaceIdOverride, enabled = true } = workspaceMemberHookOptions(options)
  const workspaceId = workspaceIdOverride ?? routeWorkspaceId
  const userId = useUserId()

  return useQuery({
    ...workspaceMemberQueryOptions(workspaceId ?? '', userId ?? ''),
    enabled: !!workspaceId && !!userId && enabled,
  })
}

/**
 * Get the current user's role in the workspace.
 */
export function useWorkspaceRole(): WorkspaceRole | null {
  const { data: member } = useWorkspaceMember()
  return member?.role ?? null
}

/**
 * Get resolved permissions for the current user, accounting for custom roles
 * and workspace-level overrides.
 */
export function useResolvedTaskPermissions(
  options?: PermissionHookOptions
): TaskPermissionConfig | null {
  const workspaceId = useWorkspaceId()
  const { data: member } = useWorkspaceMember(options)
  const { data: settings, isPending: settingsPending } =
    useWorkspaceSettings<WorkspaceSettingsWithRolePerms>(workspaceId ?? '', {
      enabled: Boolean(member) && (options?.enabled ?? true),
    })

  return useMemo(() => {
    if (!member || (!member.custom_role_id && settingsPending)) {
      return null
    }
    return applyInboxDepartmentRestriction(
      resolvePermissions(member, settings),
      member.department_members?.map(({ department_id }) => department_id)
    )
  }, [member, settings, settingsPending])
}

/**
 * Which sensitive property field groups (WiFi & access, financials, custom
 * fields) the current
 * user's role may see on the property detail page (PROD-9075). The property
 * detail route applies the same rule server-side; this hook only keeps the UI
 * from rendering empty groups for redacted fields.
 */
export function usePropertySensitiveFieldGroups(
  options?: PermissionHookOptions
): PropertySensitiveFieldGroupAccess {
  const resolved = useResolvedTaskPermissions(options)

  return useMemo(() => resolvePropertySensitiveFieldGroups(resolved), [resolved])
}

/**
 * Check if the current user has a specific permission.
 *
 * Permission resolution order:
 * 1. Custom role? Use custom_roles.permissions directly
 * 2. Built-in role? Check workspace.settings.rolePermissions override
 * 3. Fall back to hardcoded ROLE_PERMISSIONS defaults
 */
export function useHasPermission(
  resource: PermissionResource,
  action: PermissionAction,
  options?: PermissionHookOptions
): boolean {
  const { data: member, isPending } = useWorkspaceMember(options)
  const resolved = useResolvedTaskPermissions(options)

  return useMemo(() => {
    if (isPending || !member || !resolved) {
      return false
    }
    return matrixHasPermission(resolved, resource, action)
  }, [member, isPending, resolved, resource, action])
}

export function useCanUseFinancialCapability(
  capability: FinancialCapability,
  options?: PermissionHookOptions
): boolean {
  const { data: member, isPending } = useWorkspaceMember(options)
  const resolved = useResolvedTaskPermissions(options)

  return useMemo(() => {
    if (isPending || !member || !resolved) {
      return false
    }
    return canUseFinancialCapability(resolved, capability)
  }, [capability, isPending, member, resolved])
}

/**
 * Resolve the dedicated Insights Results permission. Until a role has an
 * explicit value, preserve the historical Reservations-or-Properties rule.
 */
export function useCanViewInsightsResults(options?: PermissionHookOptions): boolean {
  const resolved = useResolvedTaskPermissions(options)

  return useMemo(() => {
    if (!resolved) {
      return false
    }
    const canViewLegacyResource = [resolved.reservations, resolved.properties].some(
      (actions) => Array.isArray(actions) && actions.includes('view')
    )
    return canViewInsightsResults(resolved, canViewLegacyResource)
  }, [resolved])
}

export function useCanViewOperationsModule(
  module: OperationsModule,
  options?: PermissionHookOptions
): boolean {
  const { data: member, isPending } = useWorkspaceMember(options)
  const resolved = useResolvedTaskPermissions(options)

  return useMemo(() => {
    if (isPending || !member || !resolved) {
      return false
    }
    return canViewOperationsModule(resolved, module, member.role)
  }, [isPending, member, module, resolved])
}

/**
 * Client mirror of `memberCanViewReservationSensitiveData` (server-permissions).
 *
 * Returns `false` until the member and workspace settings have loaded so a
 * restricted role never briefly reads a privileged surface.
 *
 * Use this to stop a surface from *requesting* sensitive reservation data. It
 * is not an authorization boundary on its own: surfaces that read reservations
 * through an API get a second, authoritative server-side resolution, but the
 * ones that query Supabase directly are bounded only by RLS, which does not
 * know about this toggle.
 */
export function useCanViewReservationSensitiveData(options?: PermissionHookOptions): boolean {
  const workspaceId = useWorkspaceId()
  const { data: member, isPending } = useWorkspaceMember(options)
  const { data: settings } = useWorkspaceSettings<WorkspaceSettingsWithRolePerms>(
    workspaceId ?? '',
    { enabled: Boolean(member) && (options?.enabled ?? true) }
  )

  return useMemo(() => {
    if (isPending || !member) {
      return false
    }

    if (member.custom_role_id) {
      return canViewReservationSensitiveData(
        member.custom_roles?.permissions,
        member.custom_role_id
      )
    }
    return canViewReservationSensitiveData(settings?.rolePermissions?.[member.role], member.role)
  }, [isPending, member, settings])
}

/**
 * Get all permissions for the current user.
 * Useful for permission-based UI rendering.
 */
export function usePermissions() {
  const { data: member, isPending, isError } = useWorkspaceMember()
  const resolved = useResolvedTaskPermissions()

  return {
    permissions: resolved as Record<PermissionResource, PermissionAction[]> | null,
    role: member?.role as WorkspaceRole | undefined,
    customRole: member?.custom_roles ?? null,
    isPending,
    isError,
    isMember: !!member,
  }
}

/**
 * Check multiple permissions at once.
 * Returns an object mapping resource:action to boolean.
 */
function useHasPermissions(
  checks: Array<[PermissionResource, PermissionAction]>
): Record<string, boolean> {
  const { data: member, isPending } = useWorkspaceMember()
  const resolved = useResolvedTaskPermissions()

  return useMemo(() => {
    const result: Record<string, boolean> = {}

    for (const [resource, action] of checks) {
      const key = `${resource}:${action}`

      if (isPending || !member || !resolved) {
        result[key] = false
        continue
      }

      result[key] = matrixHasPermission(resolved, resource, action)
    }

    return result
  }, [member, isPending, resolved, checks])
}

/**
 * Check if user is at least a certain role level.
 * Role hierarchy: ADMIN > MANAGER > MEMBER > VIEWER
 * Custom roles: map to MEMBER tier
 */
export function useIsAtLeastRole(minimumRole: WorkspaceRole): boolean {
  const { data: member } = useWorkspaceMember()

  return useMemo(() => {
    if (!member) {
      return false
    }

    const hierarchy: WorkspaceRole[] = ['VIEWER', 'MEMBER', 'MANAGER', 'ADMIN']

    let effectiveRole = member.role as WorkspaceRole

    // Custom roles map to MEMBER tier
    if (member.custom_role_id && member.custom_roles) {
      effectiveRole = 'MEMBER'
    }

    const currentIndex = hierarchy.indexOf(effectiveRole)
    const minimumIndex = hierarchy.indexOf(minimumRole)

    return currentIndex >= minimumIndex
  }, [member, minimumRole])
}

/**
 * Check if user is an admin.
 */
export function useIsAdmin(): boolean {
  const role = useWorkspaceRole()
  return role === 'ADMIN'
}

/**
 * Check if user can view the full Insights analytics dashboard.
 */
export function useCanViewFullInsights(): boolean {
  return useHasPermission('insights', 'view')
}

export function useCanViewPulse(): boolean {
  const { data: member, isPending } = useWorkspaceMember()
  const resolved = useResolvedTaskPermissions()
  const canViewConversations = useHasPermission('conversations', 'view')
  const canViewContacts = useHasPermission('contacts', 'view')
  const canViewReservations = useHasPermission('reservations', 'view')

  return useMemo(() => {
    if (isPending || !member || !resolved) {
      return false
    }

    return (
      canViewConversations &&
      canViewContacts &&
      canViewReservations &&
      canViewReservationSensitiveData(resolved, member.custom_role_id ?? member.role)
    )
  }, [canViewContacts, canViewConversations, canViewReservations, isPending, member, resolved])
}

/**
 * Check whether the current user's role may see AI reasoning steps — agent
 * thinking, preflight notes, prompts, and tool arguments. Defense in depth for
 * the server-side gate in `@/lib/auth/server-permissions`; fails closed while
 * membership is still loading so restricted roles never flash the internals
 * (PROD-9072).
 */
export function useCanViewAiReasoning(): boolean {
  const { data: member } = useWorkspaceMember()
  const resolved = useResolvedTaskPermissions()

  if (!(member && resolved)) {
    return false
  }
  return canViewAiReasoning(resolved, member.custom_role_id ?? member.role)
}

/**
 * Check if user is a manager or higher.
 */
export function useIsManager(): boolean {
  return useIsAtLeastRole('MANAGER')
}

/**
 * Check if user is a worker-facing member.
 * Field-worker shell is available to older FIELD_WORKER custom roles and
 * MEMBERs who belong to field-ops departments.
 */
export function useIsFieldWorker(): boolean {
  const { data: member } = useWorkspaceMember()

  return useMemo(() => isFieldOpsWorker(member), [member])
}

/**
 * Check if user can self-claim vacant shifts in their departments.
 */
export function useHasShiftAccess(): boolean {
  const { data: member } = useWorkspaceMember()

  return useMemo(() => canSelfClaimShifts(member), [member])
}

// =============================================================================
// INBOX ACCESS
// =============================================================================

/**
 * Get resolved inbox access list for the current user.
 * Returns ["*"] for all access, [] for no access, or specific inbox slugs.
 */
export function inboxAccessQueryOptions(workspaceId: string | null | undefined) {
  const scopedWorkspaceId = workspaceId ?? ''

  return queryOptions({
    queryKey: ['inbox-access', workspaceId],
    queryFn: async (): Promise<{ canViewInbox: boolean; access: string[] }> => {
      const res = await fetch(`/api/workspaces/${scopedWorkspaceId}/inbox-v2/access`, {
        credentials: 'include',
      })
      if (!res.ok) {
        throw new Error(await responseErrorMessage(res, 'Failed to load inbox access'))
      }
      return res.json()
    },
    enabled: Boolean(workspaceId),
    staleTime: 30_000,
  })
}

export function useInboxAccessQuery() {
  const workspaceId = useWorkspaceId()

  return useQuery(inboxAccessQueryOptions(workspaceId))
}

export function useInboxAccess(): string[] {
  const { data } = useInboxAccessQuery()

  return data?.access ?? []
}

/**
 * Check if current user has access to a specific inbox by slug.
 */
function useHasInboxAccess(slug: string): boolean {
  const accessList = useInboxAccess()

  return useMemo(() => {
    if (accessList.includes('*')) {
      return true
    }
    return accessList.includes(slug)
  }, [accessList, slug])
}
